import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Signal,
  TemplateRef,
  WritableSignal,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter, take } from 'rxjs';
import { features } from '@consts/text.const';
import { routerSegments } from '@consts/routes';
import { DatabaseTagsModel } from '@models/database-tags.model';
import { IntelligenceModels } from '@models/intelligence.model';
import { DatabaseTagsActions } from '@states/database-tags/database-tags.action.types';
import { ConfirmDialogModel, ConfirmDialogService, UiButtonComponent, UiNoDataComponent } from '@ui';
import { DatasetTableConfig, UiDatasetTableComponent } from '@ui/lib/ui-kit/ui-dataset-table';
import { CreateTagDialogComponent } from '../../../../../pages/database/pages/tags/dialogs/create-tag-dialog/create-tag-dialog.component';
import { EditTagDialogComponent } from '../../../../../pages/database/pages/tags/dialogs/edit-tag-dialog/edit-tag-dialog.component';
import { IntelligenceResultsService } from '../../../../../services/intelligence-results.service';
import { UiSearchComponent } from '../../../../ui-kit/ui-search/ui-search.component';
import { TagResultCellContext, createTagResultTableConfig } from './tag-result-table.util';

type Tag = IntelligenceModels.Tags.Tag;

/** The Database → Tags page's own dialog sizing, reused verbatim for create and edit. */
const TAG_DIALOG_PANEL_CLASS = 'modal-no-padding';
const TAG_DIALOG_WIDTH = '472px';

/**
 * Cmd+K table of every organization tag, modelled on the Database → Tags page.
 * Filtering is client-side: `list-tags` returns the whole organization list in
 * one response, so there is nothing to re-query. Creation, editing and deletion
 * all reuse the page's own dialogs and store actions; the resulting tag reaches
 * this snapshot through `IntelligenceResultsService`, which listens for the
 * store success actions rather than re-running `list-tags`.
 */
@Component({
  selector: 'app-tag-result',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    UiButtonComponent,
    UiDatasetTableComponent,
    UiNoDataComponent,
    UiSearchComponent,
  ],
  templateUrl: './tag-result.component.html',
  styleUrls: ['./tag-result.component.scss'],
})
export class TagResultComponent {
  private readonly resultsService = inject(IntelligenceResultsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly matDialog = inject(MatDialog);
  private readonly store$ = inject(Store);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly translateService = inject(TranslateService);

  protected readonly defaultTagColor: string = DatabaseTagsModel.defaultTagColor;
  protected readonly noData: typeof features.tags.noData = features.tags.noData;
  protected readonly noResults: typeof features.tags.noResults = features.tags.noResults;
  protected readonly searchQuery: WritableSignal<string> = signal<string>('');

  private readonly nameCellTemplate = viewChild<TemplateRef<TagResultCellContext>>('nameCell');
  private readonly isShownInLeftPanelCellTemplate =
    viewChild<TemplateRef<TagResultCellContext>>('isShownInLeftPanelCell');

  protected readonly response: Signal<IntelligenceModels.Tags.ListResponse | null> = computed(() => {
    const result = this.resultsService.current();
    return result?.type === 'tags' ? (result.data as IntelligenceModels.Tags.ListResponse) : null;
  });

  protected readonly tags: Signal<Tag[]> = computed(() => this.response()?.tags ?? []);

  protected readonly filteredTags: Signal<Tag[]> = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    const tags = this.tags();
    return query ? tags.filter((tag) => (tag.name ?? '').toLowerCase().includes(query)) : tags;
  });

  protected readonly tableConfig: Signal<DatasetTableConfig<Tag> | null> = computed(() => {
    const nameCellTemplate = this.nameCellTemplate();
    const isShownInLeftPanelCellTemplate = this.isShownInLeftPanelCellTemplate();
    return nameCellTemplate && isShownInLeftPanelCellTemplate
      ? createTagResultTableConfig({ nameCellTemplate, isShownInLeftPanelCellTemplate })
      : null;
  });

  public constructor() {
    this.resultsService.setHeaderActions([
      {
        icon: 'ui-icon-new-tab',
        tooltip: 'TAG_RESULT.OPEN_PAGE',
        onClick: () => this.openTagsPage(),
      },
    ]);
    this.destroyRef.onDestroy(() => this.resultsService.setHeaderActions([]));
  }

  /** Tags created before the flag existed have no value; unset means shown. */
  protected isShownInLeftPanel(tag: Tag): boolean {
    return tag.isShownInLeftPanel === undefined || tag.isShownInLeftPanel === true;
  }

  protected onSearchQuery(query: string): void {
    this.searchQuery.set(query);
  }

  /**
   * Open the Database → Tags create dialog as-is. Opened directly rather than
   * through `RouteModalService`, matching the tag picker and filter: a URL param
   * would survive a refresh that the Cmd+K session itself does not.
   */
  protected openCreateDialog(): void {
    this.matDialog.open(CreateTagDialogComponent, {
      panelClass: TAG_DIALOG_PANEL_CLASS,
      width: TAG_DIALOG_WIDTH,
    });
  }

  protected onRowAction(event: IntelligenceModels.Tags.DatasetRowActionEvent): void {
    switch (event.actionId) {
      case IntelligenceModels.Tags.RowActionId.Edit:
        this.openEditDialog(event.row);
        break;
      case IntelligenceModels.Tags.RowActionId.Delete:
        this.deleteTag(event.row);
        break;
    }
  }

  /**
   * Open the Database → Tags edit dialog with the row's own values. The page
   * normalizes the pre-flag shape to "shown" before seeding the form, so the
   * dialog does not save an unrelated `false` back for a tag that never carried
   * the flag; the Cmd+K panel does the same.
   */
  private openEditDialog(tag: Tag): void {
    this.matDialog.open(EditTagDialogComponent, {
      panelClass: TAG_DIALOG_PANEL_CLASS,
      width: TAG_DIALOG_WIDTH,
      data: {
        id: tag._id,
        name: tag.name,
        color: tag.color || this.defaultTagColor,
        isShownInLeftPanel: this.isShownInLeftPanel(tag),
      },
    });
  }

  /**
   * Confirm through `ConfirmDialogService` rather than the global
   * `showConfirmModal` stream: the Cmd+K panel and the Tags page can be alive at
   * once, and a shared stream would let one confirmation drive both.
   */
  private deleteTag(tag: Tag): void {
    this.confirmDialogService
      .open({
        type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        title: this.translateService.instant('DATABASE_TAGS.DELETE_MODAL.TITLE'),
        msg: this.translateService.instant('DATABASE_TAGS.DELETE_MODAL.MESSAGE', {
          name: `"${tag.name}"`,
        }),
        confirm: this.translateService.instant('COMMON.DELETE'),
        cancel: this.translateService.instant('COMMON.CANCEL'),
        disableClose: true,
      })
      .pipe(
        take(1),
        filter(
          (result) =>
            result?.selection === ConfirmDialogModel.ConfirmResult.OK &&
            result.type === ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.store$.dispatch(DatabaseTagsActions.removeDatabaseTag({ id: tag._id })));
  }

  /** Open the page in a NEW TAB so the current Cmd+K session stays open. */
  private openTagsPage(): void {
    const tree = this.router.createUrlTree(['/', routerSegments.database, routerSegments.tags]);
    const path = this.router.serializeUrl(tree);
    const url = window.location?.origin ? `${window.location.origin}${path}` : path;
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
